import React, { useState, useEffect } from "react";
import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import dayjs from "dayjs";
import { Box, Card, Typography } from "@mui/material";
import {
  CustomDataTable,
  TableColumn,
} from "../../components/CommnTable/CustomDataTable";
import {
  AttendanceData,
  AttendanceProps,
  AttendanceRecord,
  AttendenceInterface,
  TransformedRecord,
} from "../../types/types";
import styles from "./Attendance.module.scss";
import { Dialog } from "primereact/dialog";
import { useGet } from "../../services/hooks/useGet";
import { configWithToken, GetUrl, PostUrl } from "../../api/endpointConfig";
import { useLoader } from "../../Loader/LoaderContext";
import { useMutationByMethod } from "../../services/hooks/useMutationByMethod";
import { ToastService } from "../../utils/toastService";
import { useSearchParams } from "react-router-dom";
import { ProgressBar } from "primereact/progressbar";
interface AttendanceRow {
  date: string;
  status: "Present" | "Absent";
}

interface AttendanceSummary {
  rows: AttendenceInterface[];
  presentCount: number;
  daysInMonth: number;
  percentage: number;
}
interface Student {
  id: string;
  name: string;
  attendance: string[]; // stored dates like "2025-09-01"
  reminder: string; // reminder date
}

interface AttendanceRow {
  date: string;
  status: "Present" | "Absent";
}

const AttendancePortal: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [attendance, setAttendance] = useState<string[]>([]);
  const [selectedMonth, setSelectedMonth] = useState<Date | null>(new Date());
  const [selectedYear, setSelectedYear] = useState<number>(
    new Date().getFullYear()
  );
  const [reminder, setReminder] = useState<string>("");
  const [mainData, setMainData] = useState<AttendanceProps[]>([]);
  const [mainRecords, setMainRecords] = useState<AttendanceRecord[]>([]);
  const [filterAdminData, setfilterAdminData] = useState<TransformedRecord[]>(
    []
  );
  const [filterStudentData, setfilterStudentData] = useState<
    AttendenceInterface[]
  >([]);
  const [offsetValue, setOffsetValue] = useState<number>(0);
  const [limitValue, setLimitValue] = useState<number>(20);
  const userData: any = JSON.parse(localStorage.getItem("userData") || "{}");
  const [selectedStudent, setSelectedStudent] = useState<Student | null>(null);
  const [selectedStudentView, setSelectedStudentView] =
    useState<boolean>(false);
  const [selectedRow, setSelectedRow] = useState<any>([]);
  const [queryParams, setQueryParams] = useState<any>({});
  const { showLoader, hideLoader } = useLoader();

  useEffect(() => {
    const prefix = searchParams.get("prefix");
    const text = searchParams.get("text");
    setQueryParams((prev: any) => {
      const updated = { ...prev };

      if (text && prefix) {
        updated[prefix] = text;
      } else {
        //  setQueryParams({});
      }
      return updated;
    });
  }, [searchParams]);

  const calculatePercentage = (attendance: string[], month: string) => {
    const [year, mon] = month.split("-");
    const daysInMonth = dayjs(`${year}-${mon}-01`).daysInMonth();
    let presentCount = 0;

    for (let day = 1; day <= daysInMonth; day++) {
      const dateStr = dayjs(
        `${year}-${mon}-${String(day).padStart(2, "0")}`
      ).format("YYYY-MM-DD");
      if (attendance.includes(dateStr)) presentCount++;
    }

    const percentage = parseFloat(
      ((presentCount / daysInMonth) * 100).toFixed(2)
    );

    return { percentage, presentCount, daysInMonth };
  };

  const currentMonth = dayjs().format("YYYY-MM");

  const getAttendanceApi = useGet<any>(
    `${GetUrl.getAttendance}`,
    configWithToken
  );
  const fetchAttendanceData = async () => {
    showLoader();
    try {
      const result = await getAttendanceApi.refetch();
      if (result.data) {
        const recordArray: any[] = Object.values(result.data);

        // Map through each record object
        const formattedData = recordArray.map((record: any, index: number) => ({
          name: record?.name,
          email: Object.keys(result.data)[index],
          scholarship_id: record?.scholarship_id || "",
          // Transform each attendance record if needed
          records:
            record?.records?.map((item: any) => ({
              attendance_date: item.attendance_date,
              created_at: item?.created_at,
              user_id: item.user_id,
              id: item.id,
              updated_at: item.updated_at,
            })) ?? [],
        }));
        setMainData(formattedData);
        setMainRecords(formattedData.flatMap((item) => item.records));
      }
    } catch (error: any) {
      console.log("error===================", error);
      hideLoader();
    } finally {
      hideLoader();
    }
  };
  useEffect(() => {
    fetchAttendanceData();
  }, []);

  useEffect(() => {
    const savedAttendance: string[] =
      JSON.parse(localStorage.getItem("attendance") || "[]") || [];
    setAttendance(savedAttendance);

    const savedReminder = localStorage.getItem("scholarshipReminder");
    if (savedReminder) setReminder(savedReminder);
  }, []);

  useEffect(() => {
    localStorage.setItem("attendance", JSON.stringify(attendance));
  }, [attendance]);

  const markAttendance = () => {
    const today = dayjs().format("YYYY-MM-DD");
    if (!attendance.includes(today)) {
      const updated = [...attendance, today];
      setAttendance(updated);
      // alert(`Attendance marked for ${today}`);
      postAttendanceReq();
    } else {
      alert("You have already marked attendance today.");
    }
  };

  const setScholarshipReminder = () => {
    const nextMonth = dayjs().add(1, "month").format("YYYY-MM-DD");
    setReminder(nextMonth);
    localStorage.setItem("scholarshipReminder", nextMonth);
  };

  const renderAttendance = (
    selectedMonth: string,
    attendance: string[]
  ): AttendanceSummary | null => {
    if (!selectedMonth) return null;

    const year = dayjs(selectedMonth).format("YYYY");
    const month = dayjs(selectedMonth).format("MM");
    const daysInMonth = dayjs(`${year}-${month}-01`).daysInMonth();

    const today = dayjs().format("YYYY-MM-DD");
    const rows: AttendenceInterface[] = [];
    let presentCount = 0;
    let validDays = 0; // count only up to current day

    for (let day = 1; day <= daysInMonth; day++) {
      const dateStr = dayjs(
        `${year}-${month}-${String(day).padStart(2, "0")}`
      ).format("YYYY-MM-DD");

      // Stop processing when future date reached
      if (dayjs(dateStr).isAfter(today)) continue;

      validDays++;
      const isPresent = attendance.includes(dateStr);

      if (isPresent) presentCount++;

      rows.push({
        attendenceDate: dateStr,
        status: isPresent ? "Present" : "Absent",
      });
    }

    const percentage =
      validDays > 0
        ? parseFloat(((presentCount / validDays) * 100).toFixed(2))
        : 0;

    return { rows, presentCount, daysInMonth: validDays, percentage };
  };
  const transformAttendanceData = (
    data: AttendanceData[]
  ): TransformedRecord[] => {
    const today = new Date();
    const currentYear = today.getFullYear();
    const currentMonth = today.getMonth();
    const daysPassed = today.getDate();
    return data.map((user) => {
      const currentMonthRecords = user.records.filter((rec) => {
        const recordDate = new Date(rec.attendance_date);
        return (
          recordDate.getMonth() === currentMonth &&
          recordDate.getFullYear() === currentYear
        );
      });
      const percentage = Math.round(
        (currentMonthRecords.length / daysPassed) * 100
      );
      const latestRecord = currentMonthRecords.reduce(
        (latest, current) =>
          new Date(current.updated_at) > new Date(latest.updated_at)
            ? current
            : latest,
        currentMonthRecords[0]
      );

      return {
        id: user.records[0]?.user_id || 0,
        name: user.name,
        email: user.email,
        percentage,
        updatedDate: latestRecord?.updated_at || "",
        createdDate: latestRecord?.created_at,
        scholarship_id: user.scholarship_id || "",
      };
    });
  };

  const transformAttendanceStd = (
    data: AttendanceProps[]
  ): AttendanceRecord[] => {
    const today = new Date().toISOString().split("T")[0];

    return data.flatMap((user) => {
      const userRecords = user.records.map((r) => ({
        ...r,
        attendance_date: r.attendance_date?.split
          ? r.attendance_date.split("T")[0]
          : r.attendance_date,
      }));

      const attendanceDates = userRecords.map((r) => r.attendance_date);

      return userRecords.map((record) => {
        const attendanceDate = record.attendance_date;
        const prevDate = new Date(attendanceDate);
        prevDate.setDate(prevDate.getDate() - 1);
        const prevDateStr = prevDate.toISOString().split("T")[0];
        const hasPrevDate = attendanceDates.includes(prevDateStr);
        const isToday = attendanceDate === today;

        let attendanceStatus = "Absent";
        if (isToday) {
          attendanceStatus = "Present";
        } else if (hasPrevDate) {
          attendanceStatus = "Present";
        } else {
          attendanceStatus = "Absent";
        }
        return {
          user_id: (record as any).user_id ?? (record as any).id ?? 0,
          id: (record as any).id ?? (record as any).user_id ?? 0,
          created_at:
            (record as any).created_at ??
            (record as any).updated_at ??
            new Date().toISOString(),
          updated_at:
            (record as any).updated_at ??
            (record as any).created_at ??
            new Date().toISOString(),
          attendance_date: attendanceDate,
          name: user.name,
          email: user.email,
          attendanceStatus,
        } as AttendanceRecord;
      });
    });
  };
  const currentDate = dayjs().format("YYYY-MM-DD");
  const attendanceData = transformAttendanceStd(mainData && (mainData as any));
  const attendanceDates = attendanceData.map((item) => item.attendance_date);
  const result = renderAttendance(currentDate, attendanceDates);

  const statusTemplate = (row: AttendenceInterface) => (
    <span
      style={{
        color: row.status === "Present" ? "green" : "red",
        fontWeight: "bold",
      }}
    >
      {row.status}
    </span>
  );
  const percentageTemplate = (row: Student) => {
    const { percentage, presentCount, daysInMonth } = calculatePercentage(
      row.attendance,
      currentMonth
    );
    return (
      <Button
        label={`${percentage}% (${presentCount}/${daysInMonth})`}
        className={percentage >= 75 ? "p-button-success" : "p-button-danger"}
        onClick={() => setSelectedStudent(row)}
      />
    );
  };

  const reminderTemplate = (row: Student) =>
    row.reminder ? (
      <span style={{ color: "green", fontWeight: "bold" }}>
        Reminder Set ({row.reminder})
      </span>
    ) : (
      <span style={{ color: "red", fontWeight: "bold" }}>Not Set</span>
    );
  const dateTemplate = (row: TransformedRecord) =>
    row.createdDate ? (
      <span style={{ color: "green", fontWeight: "bold" }}>
        {row.createdDate.split("T")[0]}
      </span>
    ) : (
      <span style={{ color: "red", fontWeight: "bold" }}>Not Set</span>
    );
  const percentageTemplate1 = (rowData: any) => {
    const value = rowData.percentage || 0;
    const color = value < 50 ? "#ef4444" : "#16a34a"; // red-500 / green-600

    return (
      <div className="flex items-center gap-2 w-full">
        <ProgressBar
          value={value}
          style={{
            backgroundColor: "#e5e7eb", // neutral background
          }}
          className="w-full h-3"
        ></ProgressBar>
        {/* <span className="text-sm font-semibold">{value}%</span> */}
      </div>
    );
  };
  let columns1: TableColumn<AttendenceInterface>[] = [];
  let columns2: TableColumn<TransformedRecord>[] = [];
  if (userData.role === "Admin") {
    columns2 = [
      {
        field: "scholarship_id",
        header: "Scholarship Id",
        sortable: true,
        filterable: true,

        customBody: (rowData: any) => {
          return (
            <Button
              label={rowData.scholarship_id || "N/A"}
              className="p-button-text"
              onClick={(e) => {
                e.stopPropagation();
                setSelectedStudentView(true);
                setSelectedRow(rowData);
              }}
            />
          );
        },
      },
      {
        field: "name",
        header: "Name",
        sortable: true,
        filterable: true,
      },
      {
        field: "email",
        header: "Email",
        sortable: true,
        filterable: true,
        // customBody: reminderTemplate,
      },
      // {
      //   field: "createdDate",
      //   header: "Created Date",
      //   sortable: true,
      //   filterable: true,
      //   customBody: dateTemplate,
      // },
      {
        field: "percentage",
        header: "Percentage (Month %)",
        sortable: true,
        filterable: true,
        customBody: percentageTemplate1,
      },
    ];
    if (selectedStudentView === true) {
      columns1 = [
        {
          field: "attendenceDate",
          header: "Attendence Date",
          sortable: true,
          filterable: true,
        },
        {
          field: "status",
          header: "Status",
          sortable: true,
          filterable: true,
          customBody: statusTemplate,
        },
      ];
    }
  } else if (userData.role !== "Admin" || selectedStudentView === true) {
    columns1 = [
      {
        field: "attendenceDate",
        header: "Attendence Date",
        sortable: true,
        filterable: true,
      },
      {
        field: "status",
        header: "Status",
        sortable: true,
        filterable: true,
        customBody: statusTemplate,
      },
    ];
  }
  const renderAttendanceHistory = (att: string[]) => {
    if (!selectedMonth) return [];

    const [year, mon] = dayjs(selectedMonth).format("YYYY-MM").split("-");
    const daysInMonth = dayjs(`${year}-${mon}-01`).daysInMonth();

    const rows: AttendanceRow[] = [];
    for (let day = 1; day <= daysInMonth; day++) {
      const dateStr = dayjs(
        `${year}-${mon}-${String(day).padStart(2, "0")}`
      ).format("YYYY-MM-DD");
      rows.push({
        date: dateStr,
        status: att.includes(dateStr) ? "Present" : "Absent",
      });
    }
    return rows;
  };

  const postAttendanceApi = useMutationByMethod<any>(
    "post",
    `${PostUrl.postAttendance}`,
    configWithToken
  );
  const postAttendanceReq = async () => {
    postAttendanceApi.mutate(null, {
      onSuccess: (data) => {
        ToastService.success(data.message);
        fetchAttendanceData();
      },
      onError: (err) => {
        console.error("error", err);
      },
    });
  };
  useEffect(() => {
    if (userData.role === "Admin") {
      const filtered = transformAttendanceData(mainData as any)?.filter(
        (item: any) =>
          Object.entries(queryParams).every(([key, value]) => {
            if (!value) return true;

            const itemValue = String(
              item[key as keyof TransformedRecord] || ""
            ).toLowerCase();
            const queryValue = String(value).toLowerCase(); // ✅ Explicit cast to string

            return itemValue.includes(queryValue);
          })
      );
      setfilterAdminData(filtered);
    } else {
      const filtered = result?.rows?.filter((item: any) =>
        Object.entries(queryParams).every(([key, value]) => {
          if (!value) return true;

          const itemValue = String(
            item[key as keyof AttendenceInterface] || ""
          ).toLowerCase();
          const queryValue = String(value).toLowerCase(); // ✅ Explicit cast to string

          return itemValue.includes(queryValue);
        })
      );

      setfilterStudentData(filtered as any);
    }
  }, [queryParams, mainData]);

  let resultRows: any = [];
  useEffect(() => {
    const filda = mainData.filter((m) => m.email === selectedRow.email);
    const attendanceDates1 = filda.flatMap((user) => {
      return user.records.map((r) => r.attendance_date.split("T")[0]);
    });
    resultRows = renderAttendance(currentDate, attendanceDates1);

    setfilterStudentData(resultRows?.rows);
  }, [selectedRow, selectedStudentView]);

  useEffect(() => {
    if (!selectedMonth || !selectedYear) return;

    const month = dayjs(selectedMonth).format("MM");

    if (userData.role === "Admin") {
      const filteredData = transformAttendanceData(mainData as any).filter(
        (item) => {
          const itemYear = dayjs(item.createdDate).format("YYYY");
          const itemMonth = dayjs(item.createdDate).format("MM");
          return itemYear === String(selectedYear) && itemMonth === month;
        }
      );

      setfilterAdminData(filteredData as any);
      if (selectedStudentView === true) {
        const filda = mainData.filter((m) => m.email === selectedRow.email);
        const attendanceDates1 = filda.flatMap((user) => {
          return user.records.map((r) => r.attendance_date.split("T")[0]);
        });
        resultRows = renderAttendance(currentDate, attendanceDates1);
        const filteredStdData = resultRows?.rows?.filter((item: any) => {
          const itemYear1 = dayjs(item.attendenceDate).format("YYYY");
          const itemMonth1 = dayjs(item.attendenceDate).format("MM");
          return itemYear1 === String(selectedYear) && itemMonth1 === month;
        });
        setfilterStudentData(filteredStdData as any);
      }
    } else {
      const filteredData = result?.rows?.filter((item) => {
        const itemYear = dayjs(item.attendenceDate).format("YYYY");
        const itemMonth = dayjs(item.attendenceDate).format("MM");
        return itemYear === String(selectedYear) && itemMonth === month;
      });

      setfilterStudentData(filteredData as any);
    }
  }, [selectedMonth, selectedYear]);

  const handlePageChange = (event: any) => {
    setOffsetValue(event.page * event.rows);
    setLimitValue(event.rows);
  };

  const setClearFilterState = (state: boolean) => {
    console.log("Clear filter state in parent:", state);
    if (state) {
      setQueryParams({});
    }
  };

  return (
    <Box className={styles.formSection}>
      <Card
        style={{
          padding: "1rem",
          border: 0,
          boxShadow: "none",
          background: "none",
        }}
      >
        {userData.role !== "Admin" && (
          <Box
            className="flex gap-3 mb-3 "
            sx={{
              display: "flex",
              justifyContent: "center",
              paddingBottom: "1rem",
            }}
          >
            <Button
              label="✅ Mark Today’s Attendance"
              className="p-button-success"
              onClick={markAttendance}
            />
            {/* <Button
            label="💰 Remind Scholarship Payment"
            className="p-button-info"
            onClick={setScholarshipReminder}
          /> */}
          </Box>
        )}

        {reminder && (
          <p>
            Scholarship payment reminder set for: <b>{reminder}</b>
          </p>
        )}
        <Box gap={2} display={"flex"} alignItems={"center"} justifyContent={selectedStudentView ? "space-between" : "flex-start"} >
          {selectedStudentView  && (
            <Box display={"flex"} alignItems={"center"} gap={2}>
          <Button
            icon="pi pi-arrow-left"
            label="Back"
            className="p-button-text"
            onClick={() => selectedStudentView ? setSelectedStudentView(false) : null}
          />
            <Typography variant="h6"  >
              {selectedRow.name}'s Attendance
            </Typography>
          </Box>
          )}
          <Box display={"flex"} gap={4} className={styles.calendarBox} >
            <Box gap={2} display={"flex"} alignItems={"center"}>
              <label>Select month</label>
              <Calendar
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.value as Date)}
                view="month"
                dateFormat="yy-mm"
                showIcon
              />
            </Box>
            <Box gap={2} display={"flex"} alignItems={"center"}>
              <label>Select Year</label>
              <Calendar
                value={selectedYear ? new Date(selectedYear, 0, 1) : null}
                onChange={(e) => setSelectedYear((e.value as Date)?.getFullYear())}
                view="year"
                dateFormat="yy"
                showIcon
              />
            </Box>
          </Box>
        </Box>

        {result && (
          <>
            <Card
              style={{
                padding: "1rem 0.5rem",
                border: 0,
                boxShadow: "none",
                background: "none",
              }}
            >
              <Box className={"cardBoxUI cardBoxInnerUI cardBoxUIAttendance"}>
                {userData.role === "Admin" && !selectedStudentView && (
                  <CustomDataTable<any>
                    data={filterAdminData}
                    columns={columns2}
                    tableTitle={
                      "Attendance history for " +
                      dayjs(selectedMonth).format("MMMM")
                    }
                    rowsPerPage={limitValue}
                    first={offsetValue}
                    rows={limitValue}
                    exportExcelShow={true}
                    onSearchClear={() => setQueryParams({})}
                    onPageChange={handlePageChange}
                    clearFilter={setClearFilterState}
                  />
                )}
                {selectedStudentView && (
                  <CustomDataTable<any>
                    data={filterStudentData}
                    columns={columns1}
                    tableTitle={
                      "Attendance history for " +
                      dayjs(selectedMonth).format("MMMM")
                    }
                    rowsPerPage={limitValue}
                    first={offsetValue}
                    rows={limitValue}
                    exportExcelShow={true}
                    onSearchClear={() => setQueryParams({})}
                    onPageChange={handlePageChange}
                    clearFilter={setClearFilterState}
                  />
                )}
                {userData.role !== "Admin" && (
                  <CustomDataTable<any>
                    data={filterStudentData}
                    columns={columns1}
                    tableTitle={
                      "Attendance history for " +
                      dayjs(selectedMonth).format("MMMM")
                    }
                    rowsPerPage={limitValue}
                    first={offsetValue}
                    rows={limitValue}
                    exportExcelShow={true}
                    onSearchClear={() => setQueryParams({})}
                    onPageChange={handlePageChange}
                    clearFilter={setClearFilterState}
                  />
                )}
              </Box>
            </Card>
            {userData.role !== "Admin" && (
              <h3 className="mt-3">
                Overall Attendance:{" "}
                <span
                  style={{
                    color: result.percentage >= 75 ? "green" : "red",
                    fontWeight: "bold",
                  }}
                >
                  {result.percentage}%
                </span>{" "}
                ({result.presentCount} / {result.daysInMonth} days)
              </h3>
            )}
            <Dialog
              header="Student Details"
              visible={!!selectedStudent}
              style={{ width: "50vw" }}
              modal
              onHide={() => setSelectedStudent(null)}
            >
              {selectedStudent && (
                <Card title={`Attendance Details: ${selectedStudent.name}`}>
                  <DataTable
                    value={renderAttendanceHistory(selectedStudent.attendance)}
                    stripedRows
                  >
                    <Column field="date" header="Date" />
                    <Column
                      field="status"
                      header="Status"
                      body={(row: AttendanceRow) => (
                        <span
                          style={{
                            color: row.status === "Present" ? "green" : "red",
                            fontWeight: "bold",
                          }}
                        >
                          {row.status}
                        </span>
                      )}
                    />
                  </DataTable>
                </Card>
              )}
            </Dialog>
          </>
        )}
      </Card>
    </Box>
  );
};

export default AttendancePortal;
